import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBoardLabelCatalog } from '@/entities/collab';
import { FormError } from '@/shared/forms';
import { Button, Dialog, DialogActions, LoadingState, MultiSelect } from '@/shared/ui';

import styles from './ResourceLabels.module.css';

export interface ResourceLabel {
  id: string;
  name: string;
  color: string;
}

/**
 * The ONE label picker of every workspace resource (board, database, note, group): it chooses
 * from the workspace's own label catalog, which is managed in the workspace. A label is a marker
 * for finding things; it gives or takes access only through an explicit label access rule.
 */
export function ResourceLabelsDialog({
  title,
  workspaceId,
  current,
  kind,
  pending,
  error,
  onSave,
  onClose,
}: {
  title: string;
  workspaceId: string;
  current: readonly ResourceLabel[];
  /** Groups carry their labels themselves only - the hint says so. */
  kind: 'database' | 'document' | 'group';
  pending: boolean;
  error: unknown;
  onSave: (labelIds: string[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const catalog = useBoardLabelCatalog(workspaceId);
  const [chosen, setChosen] = useState<string[]>(current.map((label) => label.id));
  const options = (catalog.data ?? []).map((label) => ({
    value: label.id,
    label: label.name,
    color: label.color,
  }));
  return (
    <Dialog title={title} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          onSave(chosen);
        }}
      >
        {catalog.isPending ? (
          <LoadingState />
        ) : options.length === 0 ? (
          <p className={styles.hint}>{t('resourceLabels.empty')}</p>
        ) : (
          <MultiSelect
            label={t('resourceLabels.field')}
            options={options}
            value={chosen}
            onChange={setChosen}
            searchPlaceholder={t('common.search')}
            emptyText={t('common.noResults')}
          />
        )}
        <p className={styles.hint}>{t(`resourceLabels.hint.${kind}`)}</p>
        <FormError error={error ?? catalog.error} />
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={pending || catalog.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** The labels of a resource as small chips (name always visible - never colour alone). */
export function ResourceLabelChips({
  labels,
  label,
  className,
}: {
  labels: readonly ResourceLabel[] | undefined;
  label: string;
  className?: string;
}) {
  if (!labels || labels.length === 0) return null;
  return (
    <ul className={[styles.chips, className].filter(Boolean).join(' ')} aria-label={label}>
      {labels.map((item) => (
        <li key={item.id} className={styles.chip} title={item.name}>
          <span aria-hidden className={styles.dot} style={{ background: item.color }} />
          <span className={styles.name}>{item.name}</span>
        </li>
      ))}
    </ul>
  );
}
